import { getAppLanguage } from "../language/dico.js";
import { normalizeListRowBindingPath } from "../layout/resolution.js";
import { resolveBehaviorFlowStateKey } from "./flowState.js";
import { resolvePath, stableValueKey } from "./method.js";
import {
  parseRuntimeVariableDependency,
  readRuntimeVariableVersion,
} from "./runtimeVariables.js";
import type { RenderCacheEntry, RuntimeContext } from "./types.js";

const immutableValueIds = new WeakMap<object, number>();
let nextImmutableValueId = 0;

function dependencyValueKey(
  ctx: RuntimeContext,
  dependency: string,
  value: unknown,
): string {
  if (
    value !== null &&
    typeof value === "object" &&
    ctx.__immutableDataSources?.has(dependency.split(".", 1)[0]!)
  ) {
    let id = immutableValueIds.get(value);
    if (id === undefined) {
      id = ++nextImmutableValueId;
      immutableValueIds.set(value, id);
    }
    return `snapshot:${id}`;
  }
  return stableValueKey(value, ctx.__signatureValueCache);
}

function readDependency(ctx: RuntimeContext, dependency: string): unknown {
  if (!dependency) {
    return undefined;
  }

  const variableId = parseRuntimeVariableDependency(dependency);
  if (variableId) {
    return readRuntimeVariableVersion(ctx, variableId);
  }

  return resolvePath(ctx, dependency);
}

/**
 * Builds a stable signature from declared data dependencies for render cache lookup.
 * Includes app language so localized display values (e.g. rack OUT/IN) invalidate on switch.
 */
export function buildDependencySignature(
  ctx: RuntimeContext,
  dependencies: string[],
): string {
  const languageKey = `lang:${getAppLanguage()}`;
  if (dependencies.length === 0) {
    return `${languageKey}|__no_deps__`;
  }

  return [
    languageKey,
    ...dependencies.map((dependency) => {
      const value = readDependency(ctx, dependency);
      return `${dependency}:${dependencyValueKey(ctx, dependency, value)}`;
    }),
  ].join("|");
}

/**
 * Normalizes a layout data-binding path into a top-level dependency key on `ctx`.
 */
export function dependencyFromDataBindingPath(path: string): string {
  return normalizeListRowBindingPath(path);
}

/**
 * Evaluates a render node once per dependency signature and instance key, reusing cached output.
 */
export function evaluateWithRenderCache<T>(
  ctx: RuntimeContext,
  nodeId: string,
  dependencies: string[],
  evaluate: () => T,
): T {
  let cache = ctx.__renderCacheByNodeKey;

  if (!cache) {
    cache = new Map<string, RenderCacheEntry>();
    ctx.__renderCacheByNodeKey = cache;
  }

  const instanceKey = resolveBehaviorFlowStateKey(nodeId, ctx);
  const signature = buildDependencySignature(ctx, dependencies);
  const cached = cache.get(instanceKey);
  const hasResumeContinuations = (ctx.__resumeContinuations?.length ?? 0) > 0;
  const hasPendingContinuations =
    (ctx.__runtimeContinuationStateByNodeKey?.get(instanceKey)?.length ?? 0) >
    0;
  // Nested nodes under an absolute parent are skipped when the parent
  // cache-hits. Any pending wait in the document must bypass cache so child
  // behaviors (and their continuation re-emit) still run.
  const hasAnyPendingContinuations =
    (ctx.__runtimeContinuationStateByNodeKey?.size ?? 0) > 0;
  const hasControlRoomDispatches =
    (ctx.__controlRoomDispatches?.length ?? 0) > 0;
  const hasListTransitionDispatches =
    (ctx.__listTransitionDispatches?.length ?? 0) > 0;
  const hasCustomDisciplineClickDispatches =
    (ctx.__customDisciplineClickDispatches?.length ?? 0) > 0;

  if (
    !hasResumeContinuations &&
    !hasPendingContinuations &&
    !hasAnyPendingContinuations &&
    !hasControlRoomDispatches &&
    !hasListTransitionDispatches &&
    !hasCustomDisciplineClickDispatches &&
    cached?.signature === signature
  ) {
    return cached.value as T;
  }

  const value = evaluate();

  cache.set(instanceKey, {
    signature,
    value,
  });

  return value;
}
